import type { FacebookAccount, FacebookAdsAccountResponse, FacebookAdsResponse, FacebookPage, FacebookPageWithInstagramAccounts, FacebookPostDetailsResponse, FacebookPostsResponse, GA4Account, GA4Property, InstagramPostDetailsResponse, InstagramPostsResponse } from 'services/local/types';
import type { BasicSuccessResponse, MetaAccount, HTTPFunctions } from '../types';

const FIELDS = [
  'id',
  'created_time',
  'message',
  'permalink_url',
  'full_picture',
  'shares',
  'reactions.summary(true)',
  'dynamic_posts',
  'comments{message,like_count,created_time,from,reactions.summary(true)}',
  'insights.metric(post_clicks,post_impressions,post_impressions_unique)',
];

const INSTAGRAM_FIELDS = [
  'id',
  'caption',
  'timestamp',
  'like_count',
  'media_url',
  'media_type',
  'comments_count',
  'insights.metric(reach,shares,impressions)',
  'comments{like_count,timestamp,user,username,text}'
];

const FACEBOOK_PICTURES = [
  'full_picture',
  'attachments{media_type,media{image,source}}'
];

const INSTAGRAM_PICTURES = [
  'media_type',
  'media_url',
  'thumbnail_url',
  'children{media_url}'
];

const ADS = [
  'name',
  'insights{spend,clicks,reach,impressions,actions,conversions,ctr,cpp,cpm,cpc}',
  'adcreatives{link_url,instagram_permalink_url,name,body,image_url,video_id,thumbnail_url,object_id,object_story_id,object_type,status,title,source_instagram_media_id,call_to_action_type,effective_instagram_media_id,effective_object_story_id,object_url,asset_feed_spec,object_story_spec}'
];

type MetaPostBody = {
  fields: string[];
  limit?: number;
  before?: string;
  after?: string;
}

const media = ({ get, post, del }: HTTPFunctions) => ({
  revokeGA4: (email: string) => del<BasicSuccessResponse>(`google-analytics/revoke/${encodeURIComponent(email)}`),
  revokeInstagram: (email: string) => del<BasicSuccessResponse>(`meta/instagram/revoke/${encodeURIComponent(email)}`),
  revokeFacebook: () => del<BasicSuccessResponse>('meta/facebook/revoke'),
  revokeTiktok: (email: string) => del<BasicSuccessResponse>(`tiktok/revoke/${encodeURIComponent(email)}`),
  sendGA4Code: (code: string, email: string, state: string) => get<GA4Account[]>(`google-analytics/callback?code=${code}&email=${encodeURIComponent(email)}&state=${state}`),
  sendInstagramCode: (code: string) => get<FacebookPageWithInstagramAccounts[]>(`meta/instagram/callback?code=${code}`),
  sendFacebookCode: (code: string) => get<FacebookPage[]>(`meta/facebook/callback?code=${code}`),
  sendTiktokCode: (code: string, email: string) => get<BasicSuccessResponse>(`tiktok/callback?code=${code}&email=${encodeURIComponent(email)}`),
  getAuthStore: (params: string) => get<BasicSuccessResponse>(`/spf/callback?${params}`),
  gA4verifyEmail: (email: string) => get<GA4Account[]>(`google-analytics/verify?email=${encodeURIComponent(email)}`),
  instagramVerifyEmail: (email: string) => get<{state: string, meta_account: FacebookPageWithInstagramAccounts[]}>(`meta/instagram/verify?email=${encodeURIComponent(email)}`),
  facebookVerifyEmail: () => get<{state: string, meta_account: FacebookPage[]}>('meta/facebook/verify'),
  tiktokVerifyEmail: (email: string) => get<MetaAccount | null>(`tiktok/verify?email=${encodeURIComponent(email)}`),
  sendGA4Account: (accountId: string, property: GA4Property, email: string) => post<BasicSuccessResponse>('google-analytics/grant/', {
    email, property_name: property.name, property_display_name: property.display_name, account_id: accountId
  }),
  selectFacebookPage: (pageId: string) => post<BasicSuccessResponse>(`/meta/facebook/grant/${pageId}`, {}),
  getFacebookAccounts: () => get<FacebookAccount[]>('/meta/me/accounts'),
  getFacebookPosts: (accountId: string, limit: number = 10, before?: string, after?: string) => {
    const body: MetaPostBody = {fields: FIELDS, limit};
    if (before) body.before = before;
    if (after) body.after = after;
    return post<FacebookPostsResponse>(`/meta/facebook/${accountId}/posts`, body);
  },
  getInstagramPosts: (accountId: string, limit: number = 10, before?: string, after?: string) => {
    const body: MetaPostBody = {fields: INSTAGRAM_FIELDS, limit};
    if (before) body.before = before;
    if (after) body.after = after;
    return post<InstagramPostsResponse>(`/meta/instagram/${accountId}/posts`, body);
  },
  getFacebookPostDetails: (postId: string) => {
    return post<FacebookPostDetailsResponse>(`/meta/post-details/${postId}`, FACEBOOK_PICTURES);
  },
  getInstagramPostDetails: (postId: string) => {
    return post<InstagramPostDetailsResponse>(`/meta/post-details/${postId}`, INSTAGRAM_PICTURES);
  },
  getFacebookAdsAccount: () => get<FacebookAdsAccountResponse>('/meta/facebook/ads/accounts'),
  getFacebookAds: (accountId: string, limit: number = 100, before?: string, after?: string) => {
    let url = `/meta/${accountId}/ads?limit=${limit}`;
    if (before) url += `&before=${before}`;
    if (after) url += `&after=${after}`;
    return post<FacebookAdsResponse>(url, ADS);
  },
  magentoLogin: (consumerKey: string) => get<BasicSuccessResponse>(`/magento/login?oauth_consumer_key=${consumerKey}`),
});

export default media;
